import { Field, Form, Formik } from "formik";
import { DateTime } from "luxon";
import { ChangeEvent, MouseEventHandler, useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { PatientGuarantor } from "pwapi";

import {
  updatePatientGuarantor,
  createPatientGuarantor,
} from "shared/actions/admin/adminPatientActions";
import {
  patientGuarantorAdminSchema,
  patientWarningSchema,
} from "shared/utils/caseSchema";
import { locationOptions } from "shared/utils/usRegions";
import {
  canValidate,
  getErrors,
  getValidationWarnings,
  getValueFromObject,
} from "shared/utils/validationUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

import {
  initialValuesForPatientGuarantor,
  shallowPatientGuarantorComparison,
} from "./guarantorFormUtils";
import FormErrors from "../../editCasePanel/FormErrors";

type GuarantorFormProps = {
  accessionCompleted?: string | null;
  guarantor: PatientGuarantor;
  patientId: number;
  onClose: MouseEventHandler<HTMLButtonElement>;
  disableSubmit?: boolean;
};

const GuarantorForm = ({
  accessionCompleted,
  guarantor,
  patientId,
  onClose,
  disableSubmit = false,
}: GuarantorFormProps) => {
  const dispatch = useDispatch();
  const [warnings, setWarnings] = useState({});

  const initialValues = initialValuesForPatientGuarantor(guarantor);

  const handleOnSubmit = useCallback(
    (values: PatientGuarantor) => {
      if (disableSubmit) return;

      const payload = {
        id: guarantor?.id,
        patient_id: patientId,
        onClose,
        ...shallowPatientGuarantorComparison(guarantor, values),
      };
      guarantor.id
        ? dispatch(updatePatientGuarantor(payload))
        : patientId && dispatch(createPatientGuarantor(payload));
    },
    [patientId, guarantor]
  );

  const canValidateForm = canValidate(
    guarantor?.patient?.clinic?.customer_type
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleOnSubmit}
      validate={(values: any) => {
        canValidateForm &&
          patientWarningSchema
            .validate(values, {
              abortEarly: false,
            })
            .then(() => {
              setWarnings({});
            })
            .catch((err) => {
              setWarnings(getValidationWarnings(err));
            });
      }}
      validateOnBlur={false}
      validationSchema={canValidateForm ? patientGuarantorAdminSchema : null}
      validateOnMount={true}
    >
      {({
        resetForm,
        dirty,
        errors,
        touched,
        values,
        initialValues,
        setValues,
      }) => {
        const allErrors = getErrors(errors, touched, !accessionCompleted, [
          "address.phone1",
          "address.phone2",
          "address.street",
          "address.zipcode",
          "date_of_birth",
          "ssn",
        ]);
        const allWarnings = getErrors(warnings, touched, !accessionCompleted);

        return (
          <Form className="AdminPanelForm">
            <div className="panelContainer">
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Guarantor Information
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="relationship_to_patient">
                    {({ field }: any) => (
                      <SelectInput
                        label="Sub Relationship"
                        options={["SELF", "SPOUSE", "PARENT", "OTHER"].map(
                          (a) => ({
                            label: a,
                            value: a,
                          })
                        )}
                        {...field}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setValues({
                            // Only reset values if the user is swapping from SELF to something else, or something else to SELF.
                            ...((values.relationship_to_patient === "SELF" &&
                              e.target.value !== "SELF") ||
                            (values.relationship_to_patient !== "SELF" &&
                              e.target.value === "SELF")
                              ? Object.keys(initialValues).reduce(
                                  (acc: any, key) => {
                                    return key === "address"
                                      ? { ...acc, [key]: {} }
                                      : { ...acc, [key]: "" };
                                  },
                                  {}
                                )
                              : values),
                            relationship_to_patient: e.target.value,
                          });
                        }}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                  <Field name="last_name">
                    {({ field }: any) => (
                      <TextInput
                        label="Last Name"
                        id="guarantor_last_name"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="first_name">
                    {({ field }: any) => (
                      <TextInput
                        label="First Name"
                        id="guarantor_first_name"
                        required
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="middle_initial">
                    {({ field }: any) => (
                      <TextInput
                        label="M.I."
                        id="guarantor_middle_initial"
                        maxLength={2}
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                </div>
                <div className="panelContainer__inputGroup">
                  <Field name="ssn">
                    {({ field }: any) => (
                      <TextInput
                        label="SSN"
                        id="ssn"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "ssn")}
                      />
                    )}
                  </Field>
                  <Field name="date_of_birth">
                    {({ field, form }: any) => (
                      <CustomInput
                        label="Date of birth"
                        id="date_of_birth"
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "date_of_birth")}
                        warning={getValueFromObject(
                          allWarnings,
                          "date_of_birth"
                        )}
                      >
                        <DateInput
                          {...field}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            form.setFieldValue("date_of_birth", e.target.value)
                          }
                          disabled={
                            disableSubmit ||
                            values.relationship_to_patient === "SELF"
                          }
                          max={DateTime.now().toISODate()}
                        />
                      </CustomInput>
                    )}
                  </Field>
                  <Field name="sex">
                    {({ field }: any) => (
                      <div style={{ width: "4em", flex: "0 0 auto" }}>
                        <SelectInput
                          id="sex"
                          label="Sex"
                          {...field}
                          options={Object.values(PatientGuarantor.sex).map(
                            (s) => ({
                              label: s,
                              value: s,
                            })
                          )}
                          disabled={
                            disableSubmit ||
                            values.relationship_to_patient === "SELF"
                          }
                        />
                      </div>
                    )}
                  </Field>
                </div>
              </fieldset>
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Guarantor Contact Information
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="address.phone1">
                    {({ field }: any) => (
                      <TextInput
                        type="tel"
                        label="Phone"
                        id="guarantor_phone1"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                        showErrorText={false}
                        error={allErrors["address.phone1"] || ""}
                      />
                    )}
                  </Field>
                  <Field name="address.phone2">
                    {({ field }: any) => (
                      <TextInput
                        type="tel"
                        label="Phone 2"
                        id="guarantor_phone2"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                        showErrorText={false}
                        error={allErrors["address.phone2"] || ""}
                      />
                    )}
                  </Field>
                </div>
              </fieldset>
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Guarantor Address
                </legend>
                <Field name="address.street">
                  {({ field }: any) => (
                    <TextInput
                      label="Street Line 1"
                      id="guarantor_street"
                      {...field}
                      disabled={
                        disableSubmit ||
                        values.relationship_to_patient === "SELF"
                      }
                    />
                  )}
                </Field>
                <Field name="address.street2">
                  {({ field }: any) => (
                    <TextInput
                      label="Street Line 2"
                      id="guarantor_street2"
                      {...field}
                      disabled={
                        disableSubmit ||
                        values.relationship_to_patient === "SELF"
                      }
                    />
                  )}
                </Field>
                <div className="panelContainer__inputGroup">
                  <Field name="address.city">
                    {({ field }: any) => (
                      <TextInput
                        label="City"
                        id="city"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="address.state">
                    {({ field }: any) => (
                      <SelectInput
                        label="State"
                        options={locationOptions}
                        {...field}
                        value={field.value || ""}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="address.zipcode">
                    {({ field }: any) => (
                      <TextInput
                        label="Zip Code"
                        id="guarantor_zipcode"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                        showErrorText={false}
                        error={allErrors["address.zipcode"] || ""}
                      />
                    )}
                  </Field>
                </div>
              </fieldset>
              <FormErrors errors={allErrors} warnings={allWarnings} />
            </div>
            <footer>
              <div className="panelContainer__inputGroup">
                <PathologyWatchButton onClick={onClose}>
                  Cancel
                </PathologyWatchButton>
              </div>

              <div className="panelContainer__inputGroup">
                <PathologyWatchButton
                  onClick={() => {
                    resetForm();
                  }}
                  disabled={!dirty}
                >
                  Discard Changes
                </PathologyWatchButton>
                <PathologyWatchButton
                  type="submit"
                  color="green"
                  disabled={
                    !dirty || disableSubmit || Object.keys(allErrors).length > 0
                  }
                >
                  Save
                </PathologyWatchButton>
              </div>
            </footer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default GuarantorForm;
